/**
 * Frequently-asked-questions page for the free, client-side build of
 * Bliksys.
 *
 * Structure mirrors the Privacy page: a centered column of native
 * `<details>/<summary>` disclosures, so keyboard navigation and a11y
 * semantics come for free, styled to match the SettingsSection pattern.
 *
 * One question — "Can an AI help me build a website for this CMS?" —
 * carries a copy-to-clipboard prompt template. That prompt teaches an AI
 * assistant everything it needs about Bliksys and the supported JSON
 * Schema subset, then leaves a slot for the user to describe their site.
 * The template text is kept out-of-line in `data/ai-website-prompt.txt`
 * and imported raw so it stays easy to edit without escaping.
 */

import { useState } from 'react';

import promptTemplate from '../data/ai-website-prompt.txt?raw';
import { useDocumentTitle } from '../hooks/useDocumentTitle.js';

import { BliksysMark } from './BliksysMark.js';
import { Icon } from './Icon.js';
import { toast } from './Toast.js';
import './Faq.css';

interface Props {
  onBack: () => void;
}

export function Faq({ onBack }: Props): JSX.Element {
  useDocumentTitle('FAQ — Bliksys');

  // Pre-open a targeted section when the URL carries its id in the hash
  // (e.g. /faq#ai-assist, handy for linking straight to the AI question).
  // Read once at mount so user toggles afterwards stick. Falls back to the
  // first section so the page always has something open.
  const initialOpenSection = window.location.hash.slice(1) || 'what-is-bliksys';

  return (
    <div className="faq">
      <header className="faq-header">
        <div className="faq-logo" aria-hidden="true">
          <BliksysMark size={32} />
        </div>
        <h1 className="faq-title">Frequently asked questions</h1>
        <p className="faq-subtitle">
          What Bliksys is, how it fits your workflow, and how to get a website built fast —
          including with an AI assistant.
        </p>
        <button type="button" className="faq-back" onClick={onBack}>
          <Icon name="arrow-left" size={16} />
          <span>Back to Bliksys</span>
        </button>
      </header>

      <section className="faq-sections">
        <FaqSection
          id="what-is-bliksys"
          question="What is Bliksys?"
          defaultOpen={initialOpenSection === 'what-is-bliksys'}
        >
          <p>
            Bliksys is a schema-driven, <strong>headless CMS</strong>. You bring a JSON Schema that
            models your content and a JSON document that holds it; Bliksys reads the schema and
            renders a fast, keyboard-first editor for that exact shape.
          </p>
          <p>
            It deliberately separates two roles: a <strong>developer</strong> authors the schema,
            and a <strong>content writer</strong> edits content against it. Bliksys stores and edits
            structured content — it does not render your public website (see{' '}
            <em>“Does Bliksys host my website?”</em> below).
          </p>
        </FaqSection>

        <FaqSection
          id="need-to-code"
          question="Do I need to know how to code or write JSON Schema?"
          defaultOpen={initialOpenSection === 'need-to-code'}
        >
          <p>
            To <strong>edit content</strong>, no. Writers open a project, fill in the form Bliksys
            generates from the schema, and save.
          </p>
          <p>
            To create the <strong>content model</strong> you need a JSON Schema — but you don&apos;t
            have to hand-write JSON. You can:
          </p>
          <ul>
            <li>
              Choose <strong>Create with the editor</strong> on the landing page and author the
              schema field by field.
            </li>
            <li>
              <strong>Import a schema file</strong> a developer handed you.
            </li>
            <li>Have an AI generate one for you — see the next question.</li>
          </ul>
        </FaqSection>

        <FaqSection
          id="ai-assist"
          question="Can an AI help me build a website for this CMS?"
          defaultOpen={initialOpenSection === 'ai-assist'}
        >
          <p>
            <strong>Yes — and it&apos;s a great fit.</strong> A Bliksys project is just a JSON
            Schema plus a JSON content document, both of which modern AI assistants (e.g. Claude)
            produce well.
          </p>
          <p>Workflow:</p>
          <ul>
            <li>Copy the prompt template below and paste it into your AI assistant.</li>
            <li>
              Fill in the <code>website brief</code> slot at the bottom with a description of your
              site, then run it.
            </li>
            <li>
              Back here, choose <strong>Import a schema file</strong> for the generated{' '}
              <code>schema.json</code>, and optionally upload the generated{' '}
              <code>content.json</code> as your starting content.
            </li>
          </ul>
          <p>
            The template teaches the assistant everything it needs about Bliksys and the supported
            JSON Schema subset, and also asks it to build a matching reference front-end so you can
            preview the rendered site.
          </p>
          <AiPromptTemplate />
        </FaqSection>

        <FaqSection
          id="what-you-get"
          question="Does Bliksys host my website?"
          defaultOpen={initialOpenSection === 'what-you-get'}
        >
          <p>
            No — Bliksys is <strong>headless</strong>. It manages your content as structured JSON;
            it doesn&apos;t host or render the public site. When you save, Bliksys can deploy that
            JSON to a target you control.
          </p>
          <p>
            Your own front-end — a static-site generator, a framework, or the reference HTML page an
            AI can generate for you — consumes that JSON and renders the actual pages. This keeps
            your content portable and your rendering stack entirely your choice.
          </p>
        </FaqSection>

        <FaqSection
          id="publish"
          question="How do I publish or get my content out?"
          defaultOpen={initialOpenSection === 'publish'}
        >
          <p>Each project has a deploy target. The free build supports:</p>
          <ul>
            <li>
              <strong>Download</strong> — a JSON file saved straight from your browser.
            </li>
            <li>
              <strong>Webhook</strong> — an HMAC-SHA256-signed <code>POST</code> to your endpoint.
            </li>
            <li>
              <strong>GitHub</strong> — commit directly to a branch, or open a pull request.
            </li>
            <li>
              <strong>Cloudflare KV</strong> — write the document to a KV namespace.
            </li>
          </ul>
          <p>Your build pipeline picks up the deployed JSON and rebuilds the site.</p>
        </FaqSection>

        <FaqSection
          id="schema-support"
          question="Which JSON Schema features are supported?"
          defaultOpen={initialOpenSection === 'schema-support'}
        >
          <p>
            A deliberate subset of <strong>JSON Schema Draft 2020-12</strong>: the common validation
            keywords (<code>type</code>, <code>properties</code>, <code>required</code>,{' '}
            <code>enum</code>, <code>const</code>, patterns, and numeric / length / array bounds),{' '}
            <code>$ref</code> + <code>$defs</code> for reuse, and <code>oneOf</code> with a{' '}
            <code>discriminator</code> for tagged content blocks.
          </p>
          <p>
            It also reads <code>x-bliksys</code> UI hints — widgets, field groups, inline layout,
            and list summaries — to shape the editor. Unsupported keywords are ignored or rejected
            rather than producing broken UI.
          </p>
        </FaqSection>

        <FaqSection
          id="images"
          question="How do I add images?"
          defaultOpen={initialOpenSection === 'images'}
        >
          <p>
            Give a string field the <code>x-bliksys</code> widget <code>&quot;image&quot;</code>. If
            you configure an <strong>asset origin</strong> (an S3-compatible bucket — e.g.
            Cloudflare R2 or Backblaze B2) on the project, that field gets a gallery picker with
            uploads; without one, it falls back to a plain URL input.
          </p>
          <p>Always include an alt-text field alongside it for accessibility.</p>
        </FaqSection>

        <FaqSection
          id="collaborate"
          question="Can my team work on a project together?"
          defaultOpen={initialOpenSection === 'collaborate'}
        >
          <p>
            The free build is <strong>single-browser</strong>: projects live in your{' '}
            <code>localStorage</code>, and you hand work off by exporting and importing content.
          </p>
          <p>
            Accounts, roles, a shared project store, and managed deploy targets are part of the
            team-oriented <strong>Bliksys Cloud</strong> (paid). Live co-editing (à la Google Docs)
            is on the roadmap, not shipped yet.
          </p>
        </FaqSection>
      </section>
    </div>
  );
}

/**
 * The copy-to-clipboard prompt block embedded in the AI question. Owns its
 * own "copied" state so the button can flip to a confirmation tick without
 * lifting state into the page. The template text is rendered as plain text
 * inside `<pre><code>`, so its angle-bracketed section tags show literally.
 */
function AiPromptTemplate(): JSX.Element {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(promptTemplate);
      setCopied(true);
      toast('Prompt copied to clipboard.', 'success');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast('Could not copy automatically — select the text and copy manually.', 'error');
    }
  };

  return (
    <div className="faq-prompt">
      <div className="faq-prompt-head">
        <span className="faq-prompt-label">Prompt template</span>
        <button type="button" className="faq-prompt-copy" onClick={() => void handleCopy()}>
          <Icon name={copied ? 'check' : 'export'} size={14} />
          <span>{copied ? 'Copied!' : 'Copy prompt'}</span>
        </button>
      </div>
      <pre className="faq-prompt-body">
        <code>{promptTemplate}</code>
      </pre>
    </div>
  );
}

/**
 * Collapsible Q&A. Native `<details>` so keyboard navigation, screen-reader
 * semantics, and click-to-expand work without JS. Visual style mirrors the
 * Privacy page's sections (same caret rotation, same card surface).
 *
 * The `id` doubles as the URL hash slug — `/faq#ai-assist` pre-opens this
 * section and the browser scrolls to it.
 */
function FaqSection({
  id,
  question,
  defaultOpen = false,
  children,
}: {
  id: string;
  question: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <details id={id} className="faq-section" open={defaultOpen}>
      <summary className="faq-section-summary">
        <span className="faq-section-title">{question}</span>
      </summary>
      <div className="faq-section-body">{children}</div>
    </details>
  );
}
